import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSeason } from '@/contexts/SeasonContext';
import { computeAttritionRows } from '@/utils/attrition';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export default function HistoryMatrix({ embedded = false, me = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeSeason, dataVersion } = useSeason();
  const initialTab = (() => {
    const t = (searchParams.get('tab') || '').toLowerCase();
    return t === 'leaderboard' || t === 'usage' || t === 'attrition' || t === 'matrix' ? t : 'matrix';
  })();
  const initialSeason = (() => {
    const sp = searchParams.get('season');
    if (sp) return sp;
    try { return localStorage.getItem('historySeason') || ''; } catch { return ''; }
  })();
  const [data, setData] = useState({ season_year: null, weeks: [], entries: [], available_seasons: [], alive_count: 0 });
  const [season, setSeason] = useState(initialSeason);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(initialTab); // matrix | leaderboard | usage | attrition
  const [query, setQuery] = useState('');
  const [teams, setTeams] = useState([]);

  // Load teams once on mount
  useEffect(() => {
    (async () => {
      try {
        const tRes = await fetch(`${API_BASE}/api/teams`);
        if (tRes.ok) {
          const tJson = await tRes.json().catch(() => []);
          setTeams(Array.isArray(tJson) ? tJson : []);
        }
      } catch {}
    })();
  }, []);

  // Fetch matrix when season or dataVersion changes
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const qp = season ? `?season=${encodeURIComponent(season)}` : '';
        const res = await fetch(`${API_BASE}/api/history/matrix${qp}`);
        const ct = res.headers.get('content-type') || '';
        const json = ct.includes('application/json') ? await res.json() : {};
        if (!res.ok) throw new Error(json.error || 'Failed to load history');
        setData(json);
        // Persist resolved default season so the dropdown has a concrete value
        if (!season && json.season_year != null) {
          setSeason(String(json.season_year));
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [season, dataVersion]);

  // Sync tab/season to URL and persist season locally
  useEffect(() => {
    if (embedded) return; // don't modify URL when embedded
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (season) next.set('season', season); else next.delete('season');
    setSearchParams(next, { replace: true });
    try { if (season) localStorage.setItem('historySeason', season); } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, season, embedded]);

  const weeks = React.useMemo(() => data.weeks || [], [data.weeks]);
  const entries = data.entries || [];

  const cellStyle = (result) => ({
    padding: '4px 6px',
    textAlign: 'center',
    borderBottom: '1px solid var(--border)',
    background: result === 'win' ? 'var(--win-bg)' : result === 'loss' ? 'var(--loss-bg)' : 'var(--tbd-bg)',
    color: result === 'win' ? 'var(--win-color)' : result === 'loss' ? 'var(--loss-color)' : 'var(--tbd-color)'
  });

  const teamInfo = (abbr) => teams.find(t => t.abbreviation === abbr) || null;
  // Format a user's full name as "Lastname F." (Lastname + First initial)
  const formatUserShort = (full) => {
    const s = (full || '').trim();
    if (!s) return '';
    const suffixes = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);
    const isSuffix = (tok) => suffixes.has(String(tok || '').toLowerCase().replace(/[.,]/g, ''));
    const stripTrailingSuffixes = (arr) => {
      const out = [...arr];
      while (out.length > 0 && isSuffix(out[out.length - 1])) out.pop();
      return out;
    };

    // Handle "Last[, Suffix], First Middle" style
    if (s.includes(',')) {
      const [lastRaw, restRaw] = s.split(',');
      // Last part may contain suffix like "Coffey Jr."
      const lastTokens = (lastRaw || '').trim().split(/\s+/).filter(Boolean);
      const cleanedLastTokens = stripTrailingSuffixes(lastTokens);
      const last = cleanedLastTokens.join(' ').trim();
      // First token from the rest (ignore any suffix accidentally placed there)
      const restTokens = ((restRaw || '').trim().split(/\s+/).filter(Boolean));
      const firstToken = restTokens.find(t => !isSuffix(t)) || '';
      const initial = firstToken ? firstToken[0].toUpperCase() + '.' : '';
      return last && initial ? `${last} ${initial}` : (last || s);
    }
    // Default: "First Middle Last [Suffix]"
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0];
    const cleaned = stripTrailingSuffixes(parts);
    if (cleaned.length === 1) return cleaned[0];
    const first = cleaned[0];
    const last = cleaned[cleaned.length - 1];
    const initial = first ? first[0].toUpperCase() + '.' : '';
    return `${last} ${initial}`.trim();
  };
  const entryMatchesQuery = (e) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (e.user_name || '').toLowerCase().includes(q) || (e.name || '').toLowerCase().includes(q);
  };
  // Determine latest week number to evaluate "active" picks
  const currentWeekNumber = React.useMemo(() => {
    let max = 0;
    for (const w of weeks) {
      const n = Number(w.week_number) || 0;
      if (n > max) max = n;
    }
    return max;
  }, [weeks]);

  // New default sort: entries with an active pick this week first, then alphabetical by user -> entry
  const aliveFirstSort = (a, b) => {
    const hasActive = (e) => {
      if (!currentWeekNumber) return false;
      const p = e && e.picks && e.picks[currentWeekNumber];
      // Active = any pick exists for the latest week (win/loss/tbd)
      return !!p;
    };
    const aActive = hasActive(a);
    const bActive = hasActive(b);
    if (aActive !== bActive) return aActive ? -1 : 1;
    // Tie-breaker: alphabetical by short-format name (Lastname F.) then entry name
    const shortA = formatUserShort(a.user_name || '');
    const shortB = formatUserShort(b.user_name || '');
    return shortA.localeCompare(shortB) || (a.name || '').localeCompare(b.name || '');
  };

  // Matrix sorting: Default to 'elim' column
  const getDefaultMatrixSort = () => 'elim';

  const [matrixSort, setMatrixSort] = useState({ column: getDefaultMatrixSort(), direction: 'desc' }); // column can be 'entry', 'elim' or week number

  // Matrix sorting functions
  const handleMatrixSort = (column) => {
    setMatrixSort(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortMatrixEntries = (entries) => {
    // Default sort: current week desc, then previous weeks desc
    if (!matrixSort.column) {
      const currentWeek = weeks.find(w => w.is_current);
      if (!currentWeek) return entries.slice().sort(aliveFirstSort);

      // Sort by current week, then cascade through previous weeks in descending order
      return entries.slice().sort((a, b) => {
        const resultPriority = { win: 3, tbd: 2, loss: 1, null: 0 };

        // Sort by weeks in descending order (current first, then previous weeks)
        const sortedWeeks = weeks.slice().sort((wa, wb) => wb.week_number - wa.week_number);

        for (const w of sortedWeeks) {
          const aCell = (a.picks && a.picks[w.week_number]) || null;
          const bCell = (b.picks && b.picks[w.week_number]) || null;

          const aPriority = aCell ? (resultPriority[aCell.result] || 0) : 0;
          const bPriority = bCell ? (resultPriority[bCell.result] || 0) : 0;

          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Descending
          }

          // Same priority, compare team abbreviations
          const aTeam = aCell ? aCell.team_abbr : '';
          const bTeam = bCell ? bCell.team_abbr : '';
          if (aTeam !== bTeam) {
            return aTeam.localeCompare(bTeam);
          }
        }

        return 0;
      });
    }

    const sorted = entries.slice().sort((a, b) => {
      let aVal, bVal;

      if (matrixSort.column === 'entry') {
        // Sort by user name then entry name
        const aUserShort = formatUserShort(a.user_name || '');
        const bUserShort = formatUserShort(b.user_name || '');
        const userCompare = aUserShort.localeCompare(bUserShort);
        if (userCompare !== 0) {
          aVal = aUserShort;
          bVal = bUserShort;
        } else {
          aVal = a.name || '';
          bVal = b.name || '';
        }
      } else if (matrixSort.column === 'elim') {
        // Sort by eliminated week (descending preference)
        // Treat null (active) as 99 so it appears at top in desc sort
        aVal = a.eliminated_week != null ? parseInt(a.eliminated_week, 10) : 99;
        bVal = b.eliminated_week != null ? parseInt(b.eliminated_week, 10) : 99;
      } else {
        // Sort by week result (week number as column)
        const weekNum = parseInt(matrixSort.column);
        const aCell = (a.picks && a.picks[weekNum]) || null;
        const bCell = (b.picks && b.picks[weekNum]) || null;

        // Sort order: win > tbd > loss > no pick
        const resultPriority = { win: 3, tbd: 2, loss: 1, null: 0 };
        const aPriority = aCell ? (resultPriority[aCell.result] || 0) : 0;
        const bPriority = bCell ? (resultPriority[bCell.result] || 0) : 0;

        if (aPriority !== bPriority) {
          aVal = aPriority;
          bVal = bPriority;
        } else {
          // Same result priority, sort by team name
          aVal = aCell ? aCell.team_abbr : '';
          bVal = bCell ? bCell.team_abbr : '';
        }
      }

      let result = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        result = aVal.localeCompare(bVal);
      } else {
        result = (aVal || 0) - (bVal || 0);
      }

      if (result !== 0) {
        return matrixSort.direction === 'desc' ? -result : result;
      }

      // Secondary sort: Entry Name (User Short + Entry Name)
      const aUserShort = formatUserShort(a.user_name || '');
      const bUserShort = formatUserShort(b.user_name || '');
      const userCompare = aUserShort.localeCompare(bUserShort);
      if (userCompare !== 0) return userCompare;

      return (a.name || '').localeCompare(b.name || '');
    });

    return sorted;
  };

  // Simple responsive sizing with debounced resize for smoother orientation changes
  const [isMobile, setIsMobile] = useState(() => {
    try { return (window.innerWidth || 0) <= 600; } catch { return false; }
  });
  const [viewportH, setViewportH] = useState(() => {
    try { return window.innerHeight || 640; } catch { return 640; }
  });

  React.useEffect(() => {
    let resizeTimer = null;
    let lastIsMobile = (() => {
      try { return (window.innerWidth || 0) <= 600; } catch { return false; }
    })();

    const onResize = () => {
      // Clear any pending resize
      if (resizeTimer) clearTimeout(resizeTimer);

      // Debounce resize events to prevent excessive re-renders during orientation change
      resizeTimer = setTimeout(() => {
        try {
          const w = window.innerWidth || 0;
          const h = window.innerHeight || 640;
          const newIsMobile = w <= 600;

          if (newIsMobile !== lastIsMobile) {
            lastIsMobile = newIsMobile;
            setIsMobile(newIsMobile);
          }
          // Always update height (keyboard show/hide, orientation, minor width changes)
          setViewportH(h);
        } catch {}
      }, 250); // Increased debounce for smoother transitions
    };

    // Set initial values
    onResize();

    // Listen for both resize and orientationchange events
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Shared table body height based on viewport; a bit shorter on phones
  const bodyHeight = useMemo(() => {
    const target = Math.floor(viewportH * (isMobile ? 0.55 : 0.65));
    const minH = isMobile ? 300 : 380;
    const maxH = isMobile ? 440 : 560;
    return Math.max(minH, Math.min(target, maxH));
  }, [viewportH, isMobile]);
  const padCell = isMobile ? '4px 6px' : '6px 8px';

  const searchPlaceholder =
    tab === 'attrition' ? 'team abbr or name'
      : tab === 'usage' ? 'team abbr or name'
        : 'user or entry';

  const TAB_DESCRIPTIONS = {
    matrix: "Every entry's weekly pick and how it turned out. Green is a win, red is a loss, blue is not yet decided; Elim is the week an entry was knocked out, and blank means still alive.",
    leaderboard: "Standings by player, combining all of that player's entries. Wins and Losses count individual weekly picks across those entries, and Entries Left is how many are still alive.",
    usage: "How often each team has been picked this season. Wins and Losses are the results of those picks, not the team's actual record.",
    attrition: 'Which teams the surviving field can still use. Entries Eligible counts alive entries that have not yet picked that team, and Eligible % is that count as a share of all alive entries.',
  };

  const tabButtons = [
    { id: 'matrix', label: 'Matrix', show: true },
    { id: 'leaderboard', label: 'Leaderboard', show: !!(me || embedded) },
    { id: 'usage', label: 'Team Usage', show: !!(me || embedded) },
    { id: 'attrition', label: 'Attrition', show: !!(me || embedded) },
  ];

  const selectedSeasonYear = Number(season) || data.season_year;
  const isArchive =
    activeSeason != null &&
    selectedSeasonYear != null &&
    Number.isFinite(Number(selectedSeasonYear)) &&
    Number(selectedSeasonYear) !== activeSeason;

  return (
    <div className={`mx-auto w-full text-left ${isMobile ? 'grid-compact max-w-full px-3 py-3' : 'max-w-[1100px] px-6 py-6'} ${embedded ? 'p-0' : ''}`}>
      {!embedded && (
        <div className="mb-4 space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">The Grid</h1>
          <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      )}

      <Card>
        <CardHeader className={`pb-3 ${isMobile ? 'px-3' : ''}`}>
          <CardTitle className="sr-only">History matrix controls</CardTitle>
          <div className="flex flex-wrap items-end gap-3">
            <div className={`space-y-1.5 ${isMobile ? 'w-full' : ''}`}>
              <Label htmlFor="grid-search">Search</Label>
              <Input
                id="grid-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className={isMobile ? 'w-full' : 'w-56'}
              />
            </div>
            <div className={`flex items-end gap-2 ${isMobile ? 'w-full' : ''}`}>
              <div className={`space-y-1.5 ${isMobile ? 'flex-1' : ''}`}>
                <Label htmlFor="grid-season">Season</Label>
                <select
                  id="grid-season"
                  aria-label="Season"
                  value={season || (data.season_year != null ? String(data.season_year) : '')}
                  onChange={(e) => setSeason(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {(Array.isArray(data.available_seasons) && data.available_seasons.length
                    ? data.available_seasons
                    : (data.season_year != null ? [data.season_year] : [])
                  ).map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
              {isArchive && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSeason(String(activeSeason))}
                >
                  Back to {activeSeason}
                </Button>
              )}
            </div>
            <div
              className={`flex flex-wrap gap-1.5 ${isMobile ? 'w-full' : 'ml-auto'}`}
              role="group"
              aria-label="Grid views"
              aria-describedby="grid-tab-description"
            >
              {tabButtons.filter((t) => t.show).map((t) => (
                <Button
                  key={t.id}
                  type="button"
                  size="sm"
                  variant={tab === t.id ? 'default' : 'outline'}
                  onClick={() => setTab(t.id)}
                  disabled={tab === t.id}
                >
                  {t.label}
                </Button>
              ))}
            </div>
          </div>
          <CardDescription id="grid-tab-description" aria-live="polite">
            {TAB_DESCRIPTIONS[tab]}
          </CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? 'px-2 pb-3' : ''}>
          {loading && (
            <div aria-busy="true" aria-live="polite" className="overflow-hidden rounded-md border border-border">
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead className="sticky">
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px' }}><div className="skeleton text" style={{ width: 120 }} /></th>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <th key={i} style={{ padding: '6px 8px' }}><div className="skeleton text" /></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      <td><div className="skeleton row" /></td>
                      {Array.from({ length: 8 }).map((_, j) => (<td key={j}><div className="skeleton row" /></td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {error && (
            <div className="flex flex-wrap items-center gap-2 text-destructive">
              <span>{error}</span>
              <Button type="button" size="sm" variant="outline" onClick={() => setSeason(season)} aria-label="Retry">
                Retry
              </Button>
            </div>
          )}
          {!loading && !error && tab === 'matrix' && (() => {
            const filteredEntries = entries.filter(entryMatchesQuery);
            const rows = sortMatrixEntries(filteredEntries);
            // minWidth is the exact column sum so table-layout:fixed overflows (scrolls) instead of crushing week cols.
            // A trailing filler column absorbs any surplus container width so Entry/week/Elim keep their declared widths.
            const firstColW = isMobile ? 100 : 200;
            const weekColW = isMobile ? 40 : 60;
            const elimColW = isMobile ? 40 : 70;
            const firstColMaxW = isMobile ? 100 : 320;
            const minW = firstColW + weeks.length * weekColW + elimColW;
            const fillerStyle = { borderBottom: '1px solid var(--border)', padding: 0 };

            // Helper to render sort indicator
            const sortIndicator = (column) => {
              if (matrixSort.column !== column) return '';
              return matrixSort.direction === 'asc' ? ' ↑' : ' ↓';
            };

            // Single table (thead+tbody). Split header/body tables misalign columns under auto sizing.
            return (
              <div className="overflow-hidden rounded-md border border-border">
                <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
                  <table role="table" aria-label="Historical matrix" className="sticky-first-col" style={{ borderCollapse: 'collapse', width: '100%', minWidth: minW, tableLayout: 'fixed' }}>
                    <thead className="sticky">
                      <tr>
                        <th scope="col"
                            style={{
                              textAlign: 'left',
                              borderBottom: '1px solid var(--border)',
                              padding: padCell,
                              minWidth: firstColW,
                              width: firstColW,
                              maxWidth: firstColMaxW,
                              cursor: 'pointer',
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden'
                            }}
                            onClick={() => handleMatrixSort('entry')}
                            title="Click to sort by entry name"
                        >
                          Entry{sortIndicator('entry')}
                        </th>
                        {weeks.map(w => (
                          <th key={w.id}
                              scope="col"
                              style={{
                                borderBottom: '1px solid var(--border)',
                                padding: padCell,
                                minWidth: weekColW,
                                width: weekColW,
                                cursor: 'pointer',
                                userSelect: 'none'
                              }}
                              onClick={() => handleMatrixSort(w.week_number)}
                              title={`Click to sort by Week ${w.week_number} results`}
                          >
                            W{w.week_number}{sortIndicator(w.week_number)}
                          </th>
                        ))}
                        <th scope="col"
                            style={{
                              borderBottom: '1px solid var(--border)',
                              padding: padCell,
                              minWidth: elimColW,
                              width: elimColW,
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                            onClick={() => handleMatrixSort('elim')}
                            title="Click to sort by elimination week"
                        >
                          Elim{sortIndicator('elim')}
                        </th>
                        <th aria-hidden="true" data-filler="true" style={fillerStyle} />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((e) => (
                        <tr key={e.id}>
                          <td role="rowheader" className="focusable" tabIndex={0}
                            title={`${e.user_name || ''}${e.user_name && e.name ? ' — ' : ''}${e.name || ''}`}
                            style={{
                              borderBottom: '1px solid var(--border)',
                              padding: padCell,
                              minWidth: firstColW,
                              width: firstColW,
                              maxWidth: firstColMaxW,
                              whiteSpace: isMobile ? 'nowrap' : 'normal',
                              wordWrap: isMobile ? 'normal' : 'break-word',
                              overflow: 'hidden'
                            }}
                          >
                            {isMobile ? (
                              <>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, lineHeight: 1.2 }}>
                                  {formatUserShort(e.user_name)}
                                </div>
                                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.7, fontSize: '10px', lineHeight: 1.2 }}>
                                  {e.name}
                                </div>
                              </>
                            ) : (
                              <div>
                                {formatUserShort(e.user_name)} <span style={{ opacity: 0.7 }}>— {e.name}</span>
                              </div>
                            )}
                          </td>
                          {weeks.map(w => {
                            const cell = (e.picks && e.picks[w.week_number]) || null;
                            const t = cell ? teamInfo(cell.team_abbr) : null;
                            const resultLabel = cell ? cell.result.toUpperCase() : '';
                            const title = cell ? `${t ? `${t.city ? t.city + ' ' : ''}${t.name} (${cell.team_abbr})` : cell.team_abbr} • ${resultLabel}` : '';
                            return (
                              <td
                                key={w.id}
                                className="focusable"
                                tabIndex={0}
                                style={{ ...cellStyle(cell?.result), padding: isMobile ? '3px 2px' : '4px 6px', minWidth: weekColW, width: weekColW }}
                                title={title}
                                aria-label={title || undefined}
                              >
                                {cell ? cell.team_abbr : ''}
                              </td>
                            );
                          })}
                          <td style={{ padding: isMobile ? '3px 2px' : '4px 6px', textAlign: 'center', borderBottom: '1px solid var(--border)', minWidth: elimColW, width: elimColW }}>
                            {e.eliminated_week ? `W${e.eliminated_week}` : ''}
                          </td>
                          <td aria-hidden="true" data-filler="true" style={fillerStyle} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          {!loading && !error && tab === 'leaderboard' && (() => {
            // Aggregate to user-level: sum wins/losses across all their entries
            const agg = new Map(); // user_id -> { user_id, user_name, wins, losses, alive, eliminated_week_max, entry_names: [], total_entries: 0, entries_remaining: 0 }
            for (const e of entries) {
              const vals = Object.values(e.picks || {});
              const wins = vals.filter(p => p.result === 'win').length;
              const losses = vals.filter(p => p.result === 'loss').length;
              const key = e.user_id != null ? e.user_id : (e.user_name || 'unknown');
              if (!agg.has(key)) {
                agg.set(key, {
                  user_id: key,
                  user_name: e.user_name || '—',
                  wins: 0,
                  losses: 0,
                  alive: false,
                  eliminated_week_max: 0,
                  entry_names: [],
                  total_entries: 0,
                  entries_remaining: 0
                });
              }
              const a = agg.get(key);
              a.wins += wins;
              a.losses += losses;
              a.total_entries += 1;
              a.entry_names.push(e.name || '');
              const isAlive = !e.eliminated_week;
              if (isAlive) a.entries_remaining += 1;
              a.alive = a.alive || isAlive;
              if (!isAlive) a.eliminated_week_max = Math.max(a.eliminated_week_max || 0, e.eliminated_week || 0);
            }
            // Filter by query: match user_name or any entry name
            const q = (query || '').trim().toLowerCase();
            const rowsAll = Array.from(agg.values());
            const rows = rowsAll.filter(r => {
              if (!q) return true;
              if ((r.user_name || '').toLowerCase().includes(q)) return true;
              return r.entry_names.some(n => (n || '').toLowerCase().includes(q));
            }).sort((a, b) => {
              if (a.alive !== b.alive) return a.alive ? -1 : 1;
              if (a.alive && b.alive) { if (b.wins !== a.wins) return b.wins - a.wins; }
              if (!a.alive && !b.alive) { if ((b.eliminated_week_max||0)!==(a.eliminated_week_max||0)) return (b.eliminated_week_max||0)-(a.eliminated_week_max||0); }
              // Final tie-breaker: alphabetical by short-format name (Lastname F.)
              const shortA = formatUserShort(a.user_name || '');
              const shortB = formatUserShort(b.user_name || '');
              return shortA.localeCompare(shortB);
            });
            const cellBorder = { borderBottom: '1px solid var(--border)', padding: padCell };
            const textCol = {
              ...cellBorder,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            };
            const metricCol = {
              ...cellBorder,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            };
            return (
              <div className="overflow-hidden rounded-md border border-border">
                <div style={{ overflowX: 'hidden', maxHeight: bodyHeight, overflowY: 'auto' }}>
                  <table
                    role="table"
                    aria-label="Leaderboard"
                    style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}
                  >
                    <thead className="sticky">
                      <tr>
                        <th scope="col" style={{ ...metricCol, textAlign: 'left' }}>{isMobile ? '#' : 'Rank'}</th>
                        <th scope="col" style={textCol}>User</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'W' : 'Wins'}</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'L' : 'Losses'}</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'Left' : 'Entries Left'}</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'Out' : 'Eliminated'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={r.user_id}>
                          <td role="rowheader" className="focusable" tabIndex={0} style={{ ...metricCol, textAlign: 'left' }}>{idx + 1}</td>
                          <td className="focusable" tabIndex={0} style={textCol}>
                            <div className={isMobile ? 'clamp-2' : undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                              {formatUserShort(r.user_name)}
                            </div>
                          </td>
                          <td style={metricCol}>{r.wins}</td>
                          <td style={metricCol}>{r.losses}</td>
                          <td style={metricCol}>{r.entries_remaining}</td>
                          <td style={metricCol}>{r.alive ? 'Alive' : (r.eliminated_week_max ? `W${r.eliminated_week_max}` : '—')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          {!loading && !error && tab === 'usage' && (() => {
            // Build pivot: team -> week_number -> count; also aggregate wins/losses per team
            const teamWeekCounts = new Map(); // abbr -> Map(week -> count)
            const teamWins = new Map(); // abbr -> wins
            const teamLosses = new Map(); // abbr -> losses
            for (const e of entries) {
              for (const [wk, p] of Object.entries(e.picks || {})) {
                const wkNum = Number(wk);
                const ab = p.team_abbr;
                if (!teamWeekCounts.has(ab)) teamWeekCounts.set(ab, new Map());
                const m = teamWeekCounts.get(ab);
                m.set(wkNum, (m.get(wkNum) || 0) + 1);
                if (p.result === 'win') teamWins.set(ab, (teamWins.get(ab) || 0) + 1);
                else if (p.result === 'loss') teamLosses.set(ab, (teamLosses.get(ab) || 0) + 1);
              }
            }
            const allRows = Array.from(teamWeekCounts.entries()).map(([abbr, map]) => {
              const wins = teamWins.get(abbr) || 0;
              const losses = teamLosses.get(abbr) || 0;
              const total = Array.from(map.values()).reduce((a, b) => a + b, 0);
              return {
                abbr,
                name: (teamInfo(abbr)?.name) || abbr,
                city: (teamInfo(abbr)?.city) || '',
                counts: map,
                wins,
                losses,
                total
              };
            }).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
            const rows = allRows.filter(r => (r.name+' '+r.city+' '+r.abbr).toLowerCase().includes(query.trim().toLowerCase()));
            const cellBorder = { borderBottom: '1px solid var(--border)', padding: padCell };
            const textCol = {
              ...cellBorder,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            };
            const metricCol = {
              ...cellBorder,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            };
            return (
              <div className="overflow-hidden rounded-md border border-border">
                <div style={{ overflowX: 'hidden', maxHeight: bodyHeight, overflowY: 'auto' }}>
                  <table
                    role="table"
                    aria-label="Team usage"
                    style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}
                  >
                    <thead className="sticky">
                      <tr>
                        <th scope="col" style={textCol}>Team</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'W' : 'Wins'}</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'L' : 'Losses'}</th>
                        <th scope="col" style={metricCol}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.abbr}>
                          <td role="rowheader" className="focusable" tabIndex={0} style={textCol}>
                            <div className={isMobile ? 'clamp-2' : undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                              {r.abbr}
                            </div>
                          </td>
                          <td style={metricCol}>{r.wins}</td>
                          <td style={metricCol}>{r.losses}</td>
                          <td style={metricCol}>{r.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          {!loading && !error && tab === 'attrition' && (() => {
            if (!teams.length) {
              return (
                <div role="alert" className="rounded-md border border-border px-3 py-3 text-sm">
                  Unable to load team list. Retry to show all teams.
                </div>
              );
            }
            const allRows = computeAttritionRows(entries, teams, data.alive_count);
            const q = query.trim().toLowerCase();
            const rows = q
              ? allRows.filter((r) => (r.name + ' ' + r.city + ' ' + r.abbr).toLowerCase().includes(q))
              : allRows;
            const cellBorder = { borderBottom: '1px solid var(--border)', padding: padCell };
            const textCol = {
              ...cellBorder,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            };
            const metricCol = {
              ...cellBorder,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            };

            return (
              <div className="overflow-hidden rounded-md border border-border">
                <div style={{ overflowX: 'hidden', maxHeight: bodyHeight, overflowY: 'auto' }}>
                  <table
                    role="table"
                    aria-label="Team attrition"
                    style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}
                  >
                    <thead className="sticky">
                      <tr>
                        <th scope="col" style={textCol}>Team</th>
                        <th scope="col" style={metricCol}>{isMobile ? 'Elig' : 'Entries Eligible'}</th>
                        <th scope="col" style={metricCol}>{isMobile ? '%' : 'Eligible %'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.abbr}>
                          <td role="rowheader" className="focusable" tabIndex={0} style={textCol}>
                            <div className={isMobile ? 'clamp-2' : undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                              {r.abbr}
                            </div>
                          </td>
                          <td style={metricCol}>{r.eligible}</td>
                          <td style={metricCol}>{r.pct == null ? '—' : `${r.pct}%`}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
